import { Alert, AlertIcon, Badge, Box, Button, Flex, Text, Textarea } from '@chakra-ui/react'
import {
  ApgarComponent,
  ApgarScore,
  BabySex,
  MinimumDeliveryData,
  MinimumDeliveryType,
} from '@hb/shared'
import { useCallback, useState } from 'react'
import { useLaborCharting } from '../context'

interface MinimumDeliveryReportStepProps {
  timeOfBirth: number
  onComplete: () => void
}

const formatDateTime = (ts: number) =>
  new Date(ts).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

// ── Radio button group ────────────────────────────────────────

function RadioGroup<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string
  value: T | undefined
  options: { id: T; text: string }[]
  onChange: (v: T) => void
}) {
  return (
    <Flex direction="column" gap={2}>
      <Text
        fontFamily="Hero-New"
        fontWeight={600}
        fontSize="sm"
        color="gray.700"
        _dark={{ color: 'gray.200' }}>
        {label}{' '}
        <Text as="span" color="red.400">
          *
        </Text>
      </Text>
      <Flex gap={2} flexWrap="wrap">
        {options.map(opt => (
          <Button
            key={opt.id}
            size="sm"
            fontFamily="Hero-New"
            colorScheme={value === opt.id ? 'pink' : 'gray'}
            variant={value === opt.id ? 'solid' : 'outline'}
            onClick={() => onChange(opt.id)}>
            {opt.text}
          </Button>
        ))}
      </Flex>
    </Flex>
  )
}

// ── Apgar scoring table ────────────────────────────────────────

const APGAR_COMPONENTS: { key: keyof Omit<ApgarScore, 'total'>; label: string }[] = [
  { key: 'heartRate', label: 'Heart Rate' },
  { key: 'respirations', label: 'Respirations' },
  { key: 'tone', label: 'Tone' },
  { key: 'reflex', label: 'Reflex' },
  { key: 'color', label: 'Color' },
]

const APGAR_VALUES: ApgarComponent[] = [0, 1, 2]

const EMPTY_APGAR: Omit<ApgarScore, 'total'> = {
  heartRate: undefined as unknown as ApgarComponent,
  respirations: undefined as unknown as ApgarComponent,
  tone: undefined as unknown as ApgarComponent,
  reflex: undefined as unknown as ApgarComponent,
  color: undefined as unknown as ApgarComponent,
}

const computeApgarTotal = (score: Omit<ApgarScore, 'total'>): number =>
  (score.heartRate ?? 0) +
  (score.respirations ?? 0) +
  (score.tone ?? 0) +
  (score.reflex ?? 0) +
  (score.color ?? 0)

const apgarColorScheme = (total: number) => {
  if (total >= 7) return 'green'
  if (total >= 4) return 'yellow'
  return 'red'
}

const ApgarInput = ({
  value,
  onChange,
}: {
  value: Omit<ApgarScore, 'total'>
  onChange: (v: Omit<ApgarScore, 'total'>) => void
}) => {
  const total = computeApgarTotal(value)
  const allSet = APGAR_COMPONENTS.every(c => value[c.key] != null)

  return (
    <Flex direction="column" gap={2}>
      <Text
        fontFamily="Hero-New"
        fontWeight={600}
        fontSize="sm"
        color="gray.700"
        _dark={{ color: 'gray.200' }}>
        Apgar — 1 Minute{' '}
        <Text as="span" color="red.400">
          *
        </Text>
      </Text>
      <Box
        borderRadius={6}
        border="1px solid"
        borderColor="gray.200"
        _dark={{ borderColor: 'whiteAlpha.200' }}
        overflow="hidden">
        {APGAR_COMPONENTS.map(({ key, label }) => (
          <Flex
            key={key}
            align="center"
            px={3}
            py={2}
            borderBottom="1px solid"
            borderColor="gray.100"
            _dark={{ borderBottomColor: 'whiteAlpha.100' }}
            gap={3}>
            <Text
              fontFamily="Hero-New"
              fontSize="sm"
              color="gray.600"
              _dark={{ color: 'gray.300' }}
              flex={1}>
              {label}
            </Text>
            <Flex gap={1.5}>
              {APGAR_VALUES.map(v => (
                <Button
                  key={v}
                  size="xs"
                  w="32px"
                  fontFamily="Hero-New"
                  fontWeight={700}
                  colorScheme={value[key] === v ? 'pink' : 'gray'}
                  variant={value[key] === v ? 'solid' : 'outline'}
                  onClick={() => onChange({ ...value, [key]: v })}>
                  {v}
                </Button>
              ))}
            </Flex>
          </Flex>
        ))}
        <Flex
          align="center"
          justify="space-between"
          px={3}
          py={2}
          bg="gray.50"
          _dark={{ bg: 'gray.750' }}>
          <Text
            fontFamily="Hero-New"
            fontSize="sm"
            fontWeight={600}
            color="gray.600"
            _dark={{ color: 'gray.300' }}>
            Total
          </Text>
          {allSet ? (
            <Badge
              colorScheme={apgarColorScheme(total)}
              fontFamily="Hero-New"
              fontSize="sm"
              px={3}
              py={1}
              borderRadius="full">
              {total} / 10
            </Badge>
          ) : (
            <Text fontFamily="Hero-New" fontSize="sm" color="gray.400">
              —
            </Text>
          )}
        </Flex>
      </Box>
    </Flex>
  )
}

// ── Main component ────────────────────────────────────────────

export const MinimumDeliveryReportStep = ({
  timeOfBirth,
  onComplete,
}: MinimumDeliveryReportStepProps) => {
  const { saveMinimumDeliveryReport } = useLaborCharting()
  const [deliveryType, setDeliveryType] = useState<MinimumDeliveryType | undefined>()
  const [babySex, setBabySex] = useState<BabySex | undefined>()
  const [apgar, setApgar] = useState<Omit<ApgarScore, 'total'>>(EMPTY_APGAR)
  const [resuscitationRequired, setResuscitationRequired] = useState<boolean | undefined>()
  const [resuscitationDescription, setResuscitationDescription] = useState('')
  const [immediateConcernFlag, setImmediateConcernFlag] = useState<boolean | undefined>()
  const [immediateConcernDescription, setImmediateConcernDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const apgarComplete = APGAR_COMPONENTS.every(c => apgar[c.key] != null)

  const canSave =
    !!deliveryType &&
    !!babySex &&
    apgarComplete &&
    resuscitationRequired != null &&
    (!resuscitationRequired || resuscitationDescription.trim().length > 0) &&
    immediateConcernFlag != null &&
    (!immediateConcernFlag || immediateConcernDescription.trim().length > 0)

  const handleSave = useCallback(async () => {
    setSaving(true)
    setError(null)
    try {
      const apgarTotal = computeApgarTotal(apgar)
      const data: MinimumDeliveryData = {
        timeOfBirth,
        deliveryType,
        babySex,
        apgar1Minute: { ...apgar, total: apgarTotal } as ApgarScore,
        resuscitationRequired: resuscitationRequired ?? false,
        resuscitationDescription: resuscitationRequired ? resuscitationDescription : undefined,
        immediateConcernFlag: immediateConcernFlag ?? false,
        immediateConcernDescription: immediateConcernFlag ? immediateConcernDescription : undefined,
        savedAt: Date.now(),
      }
      await saveMinimumDeliveryReport(data)
      onComplete()
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save. Please try again.')
    } finally {
      setSaving(false)
    }
  }, [
    timeOfBirth,
    deliveryType,
    babySex,
    apgar,
    resuscitationRequired,
    resuscitationDescription,
    immediateConcernFlag,
    immediateConcernDescription,
    saveMinimumDeliveryReport,
    onComplete,
  ])

  return (
    <Flex direction="column" gap={5} px={6} py={5} overflowY="auto" maxH="75vh">
      <Alert status="info" borderRadius={6} py={2} fontSize="sm">
        <AlertIcon />
        <Text fontSize="sm">
          Complete these six fields before closing. The full delivery record can be completed later.
        </Text>
      </Alert>

      {/* Field 1: Time of Birth — read-only confirmation */}
      <Flex direction="column" gap={1}>
        <Text
          fontFamily="Hero-New"
          fontWeight={600}
          fontSize="sm"
          color="gray.700"
          _dark={{ color: 'gray.200' }}>
          Time of Birth
        </Text>
        <Text fontFamily="Hero-New" fontSize="md" color="gray.800" _dark={{ color: 'gray.100' }}>
          {formatDateTime(timeOfBirth)}
        </Text>
      </Flex>

      {/* Field 2: Delivery Type */}
      <RadioGroup<MinimumDeliveryType>
        label="Delivery Type"
        value={deliveryType}
        onChange={setDeliveryType}
        options={[
          { id: 'spontaneous-vaginal', text: 'Spontaneous Vaginal' },
          { id: 'operative-vaginal', text: 'Operative Vaginal' },
          { id: 'cesarean', text: 'Cesarean (transfer)' },
          { id: 'precipitous', text: 'Precipitous' },
        ]}
      />

      {/* Field 3: Baby Sex */}
      <RadioGroup<BabySex>
        label="Baby Sex"
        value={babySex}
        onChange={setBabySex}
        options={[
          { id: 'male', text: 'Male' },
          { id: 'female', text: 'Female' },
          { id: 'indeterminate', text: 'Indeterminate' },
        ]}
      />

      {/* Field 4: Apgar 1 Minute */}
      <ApgarInput value={apgar} onChange={setApgar} />

      {/* Field 5: Resuscitation Required */}
      <Flex direction="column" gap={2}>
        <RadioGroup<'yes' | 'no'>
          label="Resuscitation Required"
          value={resuscitationRequired == null ? undefined : resuscitationRequired ? 'yes' : 'no'}
          onChange={v => setResuscitationRequired(v === 'yes')}
          options={[
            { id: 'yes', text: 'Yes' },
            { id: 'no', text: 'No' },
          ]}
        />
        {resuscitationRequired === true && (
          <Flex direction="column" gap={1}>
            <Text
              fontFamily="Hero-New"
              fontSize="xs"
              color="gray.600"
              _dark={{ color: 'gray.400' }}>
              Brief description required{' '}
              <Text as="span" color="red.400">
                *
              </Text>
            </Text>
            <Textarea
              placeholder="Describe resuscitation performed…"
              value={resuscitationDescription}
              onChange={e => setResuscitationDescription(e.target.value)}
              size="sm"
              fontFamily="Hero-New"
              rows={2}
            />
          </Flex>
        )}
      </Flex>

      {/* Field 6: Immediate Concern Flag */}
      <Flex direction="column" gap={2}>
        <RadioGroup<'yes' | 'no'>
          label="Immediate Concern — Maternal or Neonatal"
          value={immediateConcernFlag == null ? undefined : immediateConcernFlag ? 'yes' : 'no'}
          onChange={v => setImmediateConcernFlag(v === 'yes')}
          options={[
            { id: 'yes', text: 'Yes' },
            { id: 'no', text: 'No' },
          ]}
        />
        {immediateConcernFlag === true && (
          <Flex direction="column" gap={1}>
            <Alert status="warning" borderRadius={6} py={1.5} fontSize="xs">
              <AlertIcon boxSize={3} />
              This will route an urgent flag to the billing team.
            </Alert>
            <Textarea
              placeholder="Describe the concern…"
              value={immediateConcernDescription}
              onChange={e => setImmediateConcernDescription(e.target.value)}
              size="sm"
              fontFamily="Hero-New"
              rows={2}
            />
          </Flex>
        )}
      </Flex>

      {error && (
        <Alert status="error" borderRadius={6} py={2} fontSize="sm">
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Button
        colorScheme="pink"
        fontFamily="Hero-New"
        fontWeight={700}
        size="lg"
        isLoading={saving}
        isDisabled={!canSave}
        onClick={handleSave}>
        Save Minimum Report
      </Button>
    </Flex>
  )
}
